import QRCodeStyling from '@metrc/qr-code-styling';
import { svgToPngDataUrl } from './resvgRenderer';
import { assertQrSizeIsSafe, getQrRasterWidth, QR_DEFAULT_ERROR_CORRECTION } from './qrSizing';

export type QrRenderOptions = {
  value: string;
  width: number;
  height: number;
  marginPx: number;
  fg: string;
  bg: string;
  image?: string;
  imageWidth?: number;
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  dpi?: number;
  outputFormat?: 'pdf' | 'zpl';
};

const createQrDrawing = ({
  value, width, height, marginPx, fg, bg,
  errorCorrectionLevel = QR_DEFAULT_ERROR_CORRECTION,
  dpi = 72,
  outputFormat = 'pdf',
}: QrRenderOptions) => {
  const geometry = assertQrSizeIsSafe({
    value,
    requestedSize: Math.min(width, height),
    dpi,
    outputFormat,
    margin: marginPx,
    errorCorrectionLevel,
  });
  const qr = new QRCodeStyling({
    type: 'svg',
    width: geometry.size,
    height: geometry.size,
    margin: geometry.margin,
    roundModules: Number.isInteger(geometry.moduleSize),
    data: value || ' ',
    engine: 'legacy',
    qrOptions: {
      typeNumber: 0,
      errorCorrectionLevel,
    },
    dotsOptions: {
      type: 'square',  // Simple squares for maximum compatibility
      color: fg || '#000000',
    },
    cornersSquareOptions: {
      type: 'extra-rounded',  // Rounded outer corners on position detection patterns
    },
    backgroundOptions: {
      color: bg || '#ffffff',
      marginColor: bg || '#ffffff',
    },
  });

  return { qr, geometry };
};

export const generateQrSvg = async (options: QrRenderOptions) => {
  const { qr, geometry } = createQrDrawing(options);
  return { svg: await qr.getRawSvgString(), geometry };
};

export const generateQrVectorPath = async (options: QrRenderOptions): Promise<string> => {
  const { qr } = createQrDrawing(options);
  return qr.getRawVectorPath();
};

export const generateQrDataUrl = async (options: QrRenderOptions): Promise<string> => {
  const { svg, geometry } = await generateQrSvg(options);
  return svgToPngDataUrl(svg, getQrRasterWidth(geometry));
};
